import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Eye, Trash2, User, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import Modal from '../components/Common/Modal';
import CustomSelect from '../components/Common/CustomSelect';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { Patient } from '../types';
import { PatientFilters, PatientListResponse, getPatientsByDoctorId, getAllPatients } from '../services/patientService';


const Patients: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [filterDisease, setFilterDisease] = useState('');
  const [filterCaseStatus, setFilterCaseStatus] = useState('');
  const [filterMdtStatus, setFilterMdtStatus] = useState('');
  const [filterReviewerStatus, setFilterReviewerStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [pageSize] = useState(20);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);

  const navigate = useNavigate();

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const filters: PatientFilters = {
        search: searchTerm || undefined,
        disease: filterDisease || undefined,
        caseStatus: filterCaseStatus || undefined,
        mdtStatus: filterMdtStatus || undefined,
        reviewerStatus: filterReviewerStatus || undefined,
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
        page: currentPage,
        limit: pageSize,
      };

      let result: PatientListResponse;
      if (user?.role === ROLES.DOCTOR) {
        result = await getPatientsByDoctorId(user.id, filters);
      } else {
        result = await getAllPatients(filters);
      }
      
      setPatients(result.patients || []);
      setTotalPages(result.totalPages || 1);
      setTotalPatients(result.total || 0);
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterDisease, filterCaseStatus, filterMdtStatus, filterReviewerStatus, filterDateFrom, filterDateTo, currentPage, pageSize, user]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleFilterChange = () => {
    setCurrentPage(1);
    fetchPatients();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterDisease('');
    setFilterCaseStatus('');
    setFilterMdtStatus('');
    setFilterReviewerStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setCurrentPage(1);
  };

  const getAge = (dobString: string ) => {
    if (!dobString) return "-";
  
    const dob = new Date(dobString);
    const today = new Date();
  
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
  
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
  
    return age;
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'approved':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'open':
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'mdt_ready':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPatientOverallStatus = (patient: Patient) => {
    if (patient.mdtCases && patient.mdtCases.length > 0) {
      const latestCase = patient.mdtCases[0];
      return latestCase.status;
    }
    return 'no_case';
  };

  const handleDeletePatient = (patientId: number) => {
    setSelectedPatientId(patientId);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedPatientId) return;
  
    try {
      await api.delete(`/patient/${selectedPatientId}`);
      await fetchPatients();
  
      setDeleteModalOpen(false);
      setSelectedPatientId(null);
    } catch (err) {
      console.error("Error deleting patient:", err);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patients</h1>
        </div>
        <button
          onClick={() => navigate('/patients/new')}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Patient</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, NHS ID, or Hospital Number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
                className="input-field pl-10"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Disease/Condition</label>
                <input
                  type="text"
                  placeholder="Filter by disease..."
                  value={filterDisease}
                  onChange={(e) => setFilterDisease(e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Status</label>
                <CustomSelect
                  value={filterCaseStatus}
                  onChange={(val) => setFilterCaseStatus(val)}
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'open', label: 'Open' },
                    { value: 'under_review', label: 'Under Review' },
                    { value: 'mdt_ready', label: 'MDT Ready' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'approved', label: 'Approved' },
                  ]}
                  placeholder="All Statuses"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Has MDT Cases</label>
                <CustomSelect
                  value={filterMdtStatus}
                  onChange={(val) => setFilterMdtStatus(val)}
                  options={[
                    { value: '', label: 'All' },
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                  placeholder="All"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reviewer Status</label>
                <CustomSelect
                  value={filterReviewerStatus}
                  onChange={(val) => setFilterReviewerStatus(val)}
                  options={[
                    { value: '', label: 'All' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'completed', label: 'Completed' },
                  ]}
                  placeholder="All"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="input-field"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button onClick={clearFilters} className="btn-secondary text-sm">
                Clear Filters
              </button>
              <button onClick={handleFilterChange} className="btn-primary text-sm">
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Info */}
      <div className="flex justify-between items-center text-sm text-gray-600">
        <span>Showing {patients.length} of {totalPatients} patients</span>
        <span>Page {currentPage} of {totalPages}</span>
      </div>

      {/* Patients Table */}
      {loading ? (
        <div className="card text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading patients...</p>
        </div>
      ) : patients.length === 0 ? (
        <div className="card text-center py-12">
          <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Patients found</h3>
          <p className="text-gray-600">Try adjusting your search criteria or add a new Patient.</p>
        </div>
      ) : (
        <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  NHS ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hospital Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gender
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  DOB
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Case Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {patients.map((patient) => {
                const overallStatus = getPatientOverallStatus(patient);
                return (
                  <tr key={patient.patient_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-600">
                              {patient.forename?.[0]}{patient.surname?.[0]}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{patient.forename} {patient.surname}</div>
                          <div className="text-sm text-gray-500">{getAge(patient.date_of_birth)} years</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {patient.nhs_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {patient.hospital_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {patient.gender}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {patient.date_of_birth}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(overallStatus)}`}>
                        {overallStatus === 'no_case' ? 'No Case' : overallStatus.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-4">
                        <button 
                          onClick={() => {
                            if (!patient.patient_id) {
                              toast.error("Patient ID is missing");
                              console.error("Patient ID is missing for patient:", patient);
                              return;
                            }
                            navigate(`/patients/${patient.patient_id}`);
                          }}
                          title="View patient"
                          className="text-primary-600 hover:text-primary-900 flex items-center"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDeletePatient(patient.patient_id)}
                          title="Delete patient"
                          className="text-red-600 hover:text-red-900 flex items-center"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 rounded-lg text-sm font-medium ${
                  currentPage === pageNum
                    ? 'bg-primary-600 text-white'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <Modal title="Delete Patient" isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}
        widthClassName="max-w-md"
      >
        <p className="text-gray-700 mb-6">
          Are you sure you want to delete this patient? This action cannot be undone.
        </p>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setDeleteModalOpen(false)}
            className="btn-secondary"
          >
            Cancel
          </button>

          <button
            onClick={confirmDelete}
            className="btn-danger"
          >
            Delete
          </button>
        </div>
      </Modal>

    </div>
  );
};

export default Patients;
